import React, { useState, useEffect } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface WrikeProject {
  id: string;
  title: string;
  description: string;
  rag: string | null;
  stage: string | null;
  phase: string | null;
  deal_value: string | null;
  units: string | null;
  yield_pct: string | null;
  proclamation: string | null;
  architect: string | null;
  start_date: string | null;
  end_date: string | null;
  owners: string[];
  permalink: string | null;
  child_ids: string[];
}

interface WrikeTask {
  id: string;
  title: string;
  status: string;
  description: string;
  importance: string | null;
  dates: Record<string, string>;
  permalink: string | null;
}

interface SpaceData {
  space_id: string;
  projects: WrikeProject[];
  error?: string;
}

interface DashboardPayload {
  spaces: Record<string, SpaceData>;
  fetched_at: number;
  cache_ttl: number;
}

// ── RAG dot ───────────────────────────────────────────────────────────────────

function RagDot({ rag }: { rag: string | null }) {
  const colours: Record<string, string> = {
    Green:  '#22c55e',
    Amber:  '#f59e0b',
    Red:    '#ef4444',
    green:  '#22c55e',
    amber:  '#f59e0b',
    red:    '#ef4444',
    G:      '#22c55e',
    A:      '#f59e0b',
    R:      '#ef4444',
  };
  const colour = rag ? (colours[rag] ?? '#94a3b8') : '#94a3b8';
  return (
    <span
      style={{ backgroundColor: colour }}
      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
      title={rag ?? 'No RAG'}
    />
  );
}

// ── Task status badge ─────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Active:      'bg-blue-100 text-blue-800',
    Completed:   'bg-green-100 text-green-800',
    Cancelled:   'bg-gray-100 text-gray-500',
    Deferred:    'bg-yellow-100 text-yellow-800',
  };
  const cls = map[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

// ── Project card ──────────────────────────────────────────────────────────────

function ProjectCard({ project }: { project: WrikeProject }) {
  const [expanded, setExpanded] = useState(false);
  const [tasks, setTasks] = useState<WrikeTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);

  const toggle = async () => {
    if (!expanded && tasks.length === 0) {
      setLoadingTasks(true);
      setTaskError(null);
      try {
        const res = await fetch(`/api/wrike/dashboard/${project.id}/tasks`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setTasks(data.tasks ?? []);
      } catch (e: any) {
        setTaskError(e.message);
      } finally {
        setLoadingTasks(false);
      }
    }
    setExpanded((v) => !v);
  };

  const milestone = project.end_date
    ? new Date(project.end_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div
      className="rounded-xl border border-white/20 overflow-hidden transition-shadow hover:shadow-lg"
      style={{ background: 'rgba(255,255,255,0.06)' }}
    >
      {/* Card header — always visible */}
      <button
        onClick={toggle}
        className="w-full text-left px-4 py-4 flex items-start gap-3 focus:outline-none"
        aria-expanded={expanded}
      >
        <div className="mt-1">
          <RagDot rag={project.rag} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm leading-snug truncate">
            {project.title}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            {project.stage && (
              <span className="text-xs" style={{ color: '#60CAF3' }}>
                {project.stage}
              </span>
            )}
            {project.phase && project.phase !== project.stage && (
              <span className="text-xs text-white/50">{project.phase}</span>
            )}
          </div>
          {milestone && (
            <p className="text-xs text-white/40 mt-1">Due {milestone}</p>
          )}
        </div>
        <span className="text-white/40 text-lg leading-none select-none mt-0.5">
          {expanded ? '−' : '+'}
        </span>
      </button>

      {/* Expanded task list */}
      {expanded && (
        <div className="border-t border-white/10 px-4 py-3 space-y-2">
          {loadingTasks && (
            <p className="text-xs text-white/40 animate-pulse">Loading tasks…</p>
          )}
          {taskError && (
            <p className="text-xs text-red-400">Error: {taskError}</p>
          )}
          {!loadingTasks && !taskError && tasks.length === 0 && (
            <p className="text-xs text-white/40 italic">No tasks found.</p>
          )}
          {tasks.map((t) => (
            <div key={t.id} className="flex items-start gap-2">
              <StatusBadge status={t.status} />
              <span className="text-xs text-white/80 leading-snug">{t.title}</span>
            </div>
          ))}
          {/* Custom fields row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pt-2 border-t border-white/10">
            {project.deal_value && (
              <span className="text-xs text-white/50">
                <span className="text-white/30">Deal </span>R{project.deal_value}
              </span>
            )}
            {project.units && (
              <span className="text-xs text-white/50">
                <span className="text-white/30">Units </span>{project.units}
              </span>
            )}
            {project.yield_pct && (
              <span className="text-xs text-white/50">
                <span className="text-white/30">Yield </span>{project.yield_pct}%
              </span>
            )}
            {project.proclamation && (
              <span className="text-xs text-white/50">
                <span className="text-white/30">Proc. </span>{project.proclamation}
              </span>
            )}
            {project.architect && (
              <span className="text-xs text-white/50">
                <span className="text-white/30">Arch. </span>{project.architect}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Space section ─────────────────────────────────────────────────────────────

const SPACE_ICONS: Record<string, string> = {
  EmpiriQ:  '🏗',
  Kaalvoet: '✏️',
  Personal: '👤',
  Odimo:    '🏠',
};

function SpaceSection({ name, data }: { name: string; data: SpaceData }) {
  const icon = SPACE_ICONS[name] ?? '📁';
  const count = data.projects.length;

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        <h2 className="text-sm font-bold uppercase tracking-widest text-white/70">{name}</h2>
        <span
          className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ background: '#60CAF3', color: '#171717' }}
        >
          {count}
        </span>
      </div>

      {data.error && (
        <p className="text-xs text-red-400 px-1">Wrike error: {data.error}</p>
      )}

      {count === 0 && !data.error && (
        <p className="text-xs text-white/30 italic px-1">No projects found.</p>
      )}

      <div className="flex flex-col gap-2">
        {data.projects.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

const SPACE_ORDER = ['EmpiriQ', 'Kaalvoet', 'Personal', 'Odimo'];

export default function Dashboard() {
  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/wrike/dashboard');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: DashboardPayload = await res.json();
      setPayload(data);
      setLastFetch(new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ background: '#171717', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10"
        style={{ background: '#171717' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold tracking-tight" style={{ color: '#60CAF3' }}>
            KAELO HQ
          </span>
          <span className="text-white/30 text-sm hidden sm:inline">/ Dashboard</span>
        </div>
        <div className="flex items-center gap-3">
          {lastFetch && (
            <span className="text-xs text-white/30 hidden sm:inline">Updated {lastFetch}</span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-opacity disabled:opacity-40"
            style={{ background: '#60CAF3', color: '#171717' }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <a
            href="/principals"
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-white/20 text-white/70 hover:text-white transition-colors"
          >
            Steerco
          </a>
        </div>
      </header>

      <main className="px-4 py-6 md:px-8 max-w-screen-xl mx-auto">
        {/* Loading skeleton */}
        {loading && !payload && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3">
                <div className="h-5 w-24 rounded bg-white/10 animate-pulse" />
                {[1, 2, 3].map((j) => (
                  <div key={j} className="h-16 rounded-xl bg-white/5 animate-pulse" />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-5 text-red-300 text-sm">
            Failed to load dashboard: {error}
            <button onClick={load} className="ml-4 underline opacity-70 hover:opacity-100">
              Retry
            </button>
          </div>
        )}

        {/* Spaces grid */}
        {payload && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {SPACE_ORDER.map((name) => {
              const data = payload.spaces[name];
              if (!data) return null;
              return <SpaceSection key={name} name={name} data={data} />;
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8 text-center text-xs text-white/20">
        EmpiriQ Holdings · Zikhethele · Kaalvoet Studio
      </footer>
    </div>
  );
}
